/* RecordPaymentDialog — standalone quick-pay dialog.
 *
 * Extracted from InvoiceDetailDialog so the doctor can record a payment
 * directly from a list row (Patient Profile → Billing, Sidebar Billing)
 * without first having to open the full invoice detail drawer. The
 * extracted form is identical to the original — same fields, same API
 * call, same toasts — just usable in isolation.
 */
import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Money, Receipt } from "@phosphor-icons/react";


const PAYMENT_METHODS = [
    { value: "cash",          label: "Cash" },
    { value: "upi",           label: "UPI" },
    { value: "card",          label: "Card" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "cheque",        label: "Cheque" },
    { value: "other",         label: "Other" },
];

function formatINR(amount) {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 2,
    }).format(amount);
}


export default function RecordPaymentDialog({ open, invoice, onClose, onSaved }) {
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("upi");
    const [reference, setReference] = useState("");
    const [note, setNote] = useState("");
    const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && invoice) {
            // Pre-fill with the remaining balance — most common case.
            setAmount(String(invoice.balance || ""));
            setMethod("upi");
            setReference("");
            setNote("");
            setPaidAt(new Date().toISOString().slice(0, 10));
        }
    }, [open, invoice]);

    const save = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt === 0) return;
        setSubmitting(true);
        try {
            await api.post(`/doctor/invoices/${invoice.id}/payments`, {
                amount: amt,
                method,
                paid_at: paidAt,
                reference: reference.trim(),
                note: note.trim(),
            });
            toast.success(`Payment recorded: ${formatINR(amt)}`);
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to record payment");
        } finally {
            setSubmitting(false);
        }
    };

    if (!invoice) return null;
    const balance = invoice.balance || 0;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="record-payment-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Money size={18} weight="duotone" className="text-primary" />
                        Record payment
                    </DialogTitle>
                    <DialogDescription>
                        <span className="inline-flex items-center gap-1.5">
                            <Receipt size={11} weight="duotone" />
                            <span className="font-mono">{invoice.code}</span>
                            <span className="text-muted-foreground">·</span>
                            <span>{invoice.patient_name || "Patient"}</span>
                        </span>
                        <br />
                        Balance owed: <span className="font-medium tabular-nums">{formatINR(balance)}</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    <div>
                        <Label htmlFor="pay-amount" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Amount</Label>
                        <Input
                            id="pay-amount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 text-lg tabular-nums"
                            data-testid="pay-amount"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="pay-method" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Method</Label>
                            <select
                                id="pay-method"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                                data-testid="pay-method"
                            >
                                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="pay-date" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Date</Label>
                            <Input
                                id="pay-date"
                                type="date"
                                value={paidAt}
                                onChange={(e) => setPaidAt(e.target.value)}
                                className="mt-1"
                                data-testid="pay-date"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="pay-reference" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Reference (optional)</Label>
                        <Input
                            id="pay-reference"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="UPI ref, cheque no, etc."
                            className="mt-1"
                            data-testid="pay-reference"
                        />
                    </div>
                    <div>
                        <Label htmlFor="pay-note" className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Note (optional)</Label>
                        <Textarea
                            id="pay-note"
                            rows={2}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="mt-1 text-sm"
                            data-testid="pay-note"
                        />
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={!parseFloat(amount) || submitting}
                        className="rounded-full"
                        data-testid="pay-confirm"
                    >
                        {submitting ? "Saving…" : "Record payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
